import { Component, EventEmitter, Input, OnInit, Output, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RolesPermissionsStore } from '../../../core/stores/roles-permissions.store';
import { LoaderComponent } from '../loader/loader.component';
import { SpinnerComponent } from '../spinner/spinner.component';

@Component({
  selector: 'app-manage-roles-modal',
  standalone: true,
  imports: [CommonModule, LoaderComponent, SpinnerComponent],
  template: `
    <div class="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4 bg-gray-900/60 backdrop-blur-sm"
         role="dialog" aria-modal="true" aria-labelledby="manageRolesTitle">
      <div class="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-lg w-full flex flex-col max-h-[92dvh] sm:max-h-[85vh]">
        <div class="sm:hidden mx-auto mt-3 h-1 w-10 rounded-full bg-gray-300 flex-shrink-0"></div>
        <!-- Header -->
        <div class="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 border-b border-gray-100">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2 id="manageRolesTitle" class="text-lg font-bold text-gray-900">Manage role</h2>
              <p class="text-xs text-gray-500 mt-0.5">Select the role for this user. Only one role can be assigned.</p>
            </div>
            <button type="button" (click)="close.emit()"
                    class="text-gray-400 hover:text-gray-600" aria-label="Close">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Roles list -->
        <div class="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 space-y-2">
          @if (loading()) {
            <app-loader caption="Loading roles…"></app-loader>
          } @else if (loadError()) {
            <div class="py-6 text-center text-sm text-red-600">{{ loadError() }}</div>
          } @else if (roles().length === 0) {
            <div class="py-6 text-center text-sm text-gray-500">No roles available.</div>
          } @else {
            @for (r of roles(); track r.roleCode) {
              <label class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors"
                     [class.border-blue-300]="isSelected(r.roleCode)"
                     [class.bg-blue-50]="isSelected(r.roleCode)"
                     [class.border-gray-200]="!isSelected(r.roleCode)"
                     [class.hover:bg-gray-50]="!isSelected(r.roleCode)">
                <input type="radio" name="roleCode" class="mt-0.5 w-4 h-4 text-blue-600"
                       [checked]="isSelected(r.roleCode)"
                       (change)="select(r.roleCode)"/>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <p class="text-sm font-medium text-gray-900">{{ r.name }}</p>
                    @if (r.isSystemRole) {
                      <span class="px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded bg-gray-100 text-gray-600">System</span>
                    }
                  </div>
                  @if (r.description) {
                    <p class="text-xs text-gray-500 mt-0.5">{{ r.description }}</p>
                  }
                  <p class="text-[11px] text-gray-400 mt-1">
                    Code {{ r.roleCode }} · {{ r.permissionCount }} permission{{ r.permissionCount === 1 ? '' : 's' }}
                  </p>
                </div>
              </label>
            }
          }
        </div>

        <!-- Footer -->
        <div class="px-4 sm:px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-4 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <button type="button" (click)="close.emit()"
                  class="w-full sm:w-auto max-md:tap-target px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
            Cancel
          </button>
          <button type="button" (click)="onSave()"
                  [disabled]="!dirty() || saving() || selectedCode() == null"
                  class="w-full sm:w-auto max-md:tap-target inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300
                         disabled:cursor-not-allowed text-white rounded-lg transition-colors">
            @if (saving()) { <app-spinner size="xs" colour="white"></app-spinner> }
            {{ saving() ? 'Saving…' : 'Save changes' }}
          </button>
        </div>
      </div>
    </div>
  `
})
export class ManageRolesModalComponent implements OnInit {
  @Input({ required: true }) currentRoleCodes: number[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<number[]>();

  private rolesStore = inject(RolesPermissionsStore);

  loading = signal(true);
  loadError = signal<string | null>(null);
  saving = signal(false);
  roles = computed(() => this.rolesStore.roles());
  selectedCode = signal<number | null>(null);

  private initialSelectedCode = signal<number | null>(null);
  dirty = computed(() => this.selectedCode() !== this.initialSelectedCode());

  async ngOnInit(): Promise<void> {
    const initial = this.currentRoleCodes.length > 0 ? this.currentRoleCodes[0] : null;
    this.selectedCode.set(initial);
    this.initialSelectedCode.set(initial);
    try {
      await this.rolesStore.loadOnce();
    } catch {
      this.loadError.set('Failed to load roles');
    }
    this.loading.set(false);
  }

  isSelected(code: number): boolean { return this.selectedCode() === code; }

  select(code: number): void {
    this.selectedCode.set(code);
  }

  onSave(): void {
    const code = this.selectedCode();
    if (!this.dirty() || this.saving() || code == null) return;
    this.saving.set(true);
    this.save.emit([code]);
  }
}
